import { Component, useEffect, useMemo, useRef, useState } from "react";
import { RiPencilLine } from "react-icons/ri";
import { useOutsideClick } from "../hooks/useOutsideClick";
import InputDynamicWidth from "./inputs/InputDynamicWidth";

type SmallInlineEditableProps = {
  value: string | number;
  setValue: (value: string | number) => any;
  className?: string;
  placeholder?: string | number;
  label?: string | Component;
  options?: { value: string | number; subtitle?: string }[];
  placeholderWidth?: number;
  onIntermediateUpdate?: (newValue: string) => void;
  width?: number;
  submitOnBlur?: boolean;
  clearAfterSubmit?: boolean;
  hidePen?: boolean;
  tagLook?: boolean;

  [x: string | number | symbol]: unknown;
};

export function SmallInlineEditable({
  value,
  setValue,
  onIntermediateUpdate,
  xs,
  hidePen,
  tagLook,
  clearAfterSubmit,
  fontWidth = 5,
  width,
  submitOnBlur = true,
  editable,
  className,
  options,
  placeholderWidth,
  placeholder,
  label,
  ...rest
}: SmallInlineEditableProps) {
  const [intermediate, setIntermediate] = useState(value || "");
  useEffect(() => setIntermediate(value || ""), [value]);

  const condensedOptions = useMemo(() => {
    let condensed = [];
    options?.forEach(({ value, subtitle }) => {
      let existingIdx = condensed.findIndex((copt) => copt.value === value);
      if (existingIdx == -1) {
        condensed.push({ value, subtitle });
      } else {
        let newCondensed = { ...condensed[existingIdx] };
        if (subtitle)
          newCondensed.subtitle = `${newCondensed.subtitle ? newCondensed.subtitle + " & " : ""}${subtitle}`;
        condensed[existingIdx] = newCondensed;
      }
    });
    return condensed;
  }, [options]);
  const inputRef = useRef<any>(null);
  const [focused, setFocused] = useState(false);
  const showPlaceholder = !focused && (!intermediate || intermediate.length === 0) && editable;
  const showOptions = !!(focused && condensedOptions?.length) && editable;
  const [loading, setLoading] = useState(false);
  const submit = async (newVal = null) => {
    const valueToSubmit = newVal || intermediate;
    if (clearAfterSubmit) {
      setIntermediate(value || "");
    }
    setFocused(false);
    inputRef.current.blur();
    setLoading(true);
    try {
      await setValue(valueToSubmit);
    } catch (e) {
      // todo: display error
      console.error(e);
      inputRef?.current?.blur() || setIntermediate(value);
    }

    setLoading(false);
  };

  const handleKeyDown = (e) =>
    e.key === "Enter" ? submit() : e.key === "Escape" ? inputRef?.current?.blur() || setIntermediate(value) : null;

  const conRef = useRef();
  const borderRadius = tagLook ? ".375rem" : 15;
  useOutsideClick(conRef, () => {
    if (focused) {
      if (submitOnBlur) {
        submit();
      } else {
        inputRef.current.blur();
        setIntermediate(value || "");
        setFocused(false);
      }
    }
  });

  const minWidth = showPlaceholder ? placeholderWidth : width || intermediate?.length * fontWidth || 0;
  return (
    <span
      onClick={() => !focused && inputRef.current.focus()}
      ref={conRef}
      className={`${loading && "text-loading"} 
      relative inline-flex items-center max-w-full overflow-hidden ${
        editable && "hover:bg-bgactive_ active:bg-bgactive_ focus:bg-bgactive_"
      } 
       cursor-text ${tagLook ? "h-6" : ""} px-2 py-1 text-sm  placeholder:text-muted ${focused && "bg-bgactive_"}
       ${showPlaceholder ? "text-muted" : ""} ${focused && condensedOptions?.length && "rounded-b-none"}`}
      style={{
        borderRadius,
        minWidth: showOptions
          ? Math.max(intermediate?.length > 0 ? intermediate?.length * fontWidth : placeholderWidth, 85)
          : null,
        borderBottomLeftRadius: showOptions ? 0 : borderRadius,
        borderBottomRightRadius: showOptions ? 0 : borderRadius,
      }}
    >
      <InputDynamicWidth
        className={`${className} bg-transparent appearance-none outline-none border-none `}
        type={"number"}
        disabled={!editable}
        // minWidth={32}
        fontSize={showPlaceholder ? "0" : undefined}
        minWidth={minWidth}
        ref={inputRef}
        onChange={(e) => {
          setIntermediate(e.target.value);
          onIntermediateUpdate && onIntermediateUpdate(e.target.value);
        }}
        value={showPlaceholder ? placeholder : intermediate}
        setValue={setValue}
        onFocus={() => {
          setFocused(true);
          inputRef.current.setSelectionRange(0, inputRef.current.value.length);
        }}
        onKeyDown={handleKeyDown}
        {...rest}
      />

      {label && <>&nbsp;{label}</>}
      {editable && !hidePen && <RiPencilLine className={`w-0 ml-1 group-hover:w-4 transition-all transition-delay`} />}
      {showOptions && (
        <div className="w-full group absolute z-20  left-0 top-full rounded-b-md">
          {condensedOptions
            .filter((o) => o.value !== undefined)
            .map((option, idx) => (
              <div
                key={`opt${option.value}#${idx}`}
                onClick={() => {
                  setIntermediate(option.value);
                  submit(option.value);
                }}
                className={`cursor-pointer ${xs ? "px-1.5 py-0.5" : "px-2 py-1"} bg-bgactive_ hover:bg-bgactive__ ${
                  idx === condensedOptions.length - 1 ? "rounded-b-md" : ""
                }`}
              >
                <span style={{ fontSize: 12, overflowWrap: "anywhere" }} className={xs ? "text-xs" : `text-sm`}>
                  {option.value}
                </span>
                <span
                  style={{ fontSize: 10, overflowWrap: "anywhere" }}
                  className="text-light-gray  opacity-50 whitespace-nowrap "
                >
                  {" "}
                  {option.subtitle}
                </span>
              </div>
            ))}
        </div>
      )}
    </span>
  );
}
